import React from 'react';
import { Card, Row, Col, Statistic, Button, Modal, message, Tag, Space } from 'antd';
import { PageLayout } from '../../ui';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import { 
  UserOutlined, 
  SettingOutlined, 
  DatabaseOutlined, 
  SecurityScanOutlined 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const AdministrationPage: React.FC = () => {
  const navigate = useNavigate();

  const [usersCount, setUsersCount] = React.useState<number | undefined>(undefined);
  const [activeSessions, setActiveSessions] = React.useState<number | undefined>(undefined);
  const [dbSize, setDbSize] = React.useState<string | number>('—');
  const [backupStatus, setBackupStatus] = React.useState<{ startedAt?: string; finishedAt?: string; ok?: boolean; message?: string } | null>(null);
  const [backupLoading, setBackupLoading] = React.useState(false);

  // Единая функция получения токена (новый ключ + старые для совместимости)
  const getAuthToken = React.useCallback(() => {
    return (
      localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken')
    );
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(`${API_BASE_URL}/system/metrics`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: 'include'
        });
        const json = await res.json();
        if (!res.ok || !json?.success) return;
        const data = json.data || {};
        if (typeof data.usersCount === 'number') setUsersCount(data.usersCount);
        if (typeof data.activeSessions === 'number') setActiveSessions(data.activeSessions);
        if (data.dbSizePretty) setDbSize(data.dbSizePretty);
      } catch {}
    })();
  }, [getAuthToken]);

  const openUsers = () => navigate('/administration/users');
  const openRolesAccess = () => navigate('/administration/roles-access');
  const openAudit = () => navigate('/administration/audit');

  const handlePurge = async () => {
    Modal.confirm({
      title: 'Очистить данные системы?',
      content: 'Будут удалены заявки, маршруты, грузы, документы и финансы. Справочники, ТН и Доверенности останутся. Действие необратимо.',
      okText: 'Да, очистить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const token = getAuthToken();
          const res = await fetch(`${API_BASE_URL}/maintenance/purge?confirm=true`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            credentials: 'include'
          });
          const json = await res.json();
          if (!res.ok || !json?.success) throw new Error(json?.error || 'Ошибка очистки');
          message.success('Очистка завершена');
        } catch (e: any) {
          message.error(e?.message || 'Ошибка очистки');
        }
      }
    });
  };

  const pollBackupStatus = React.useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/maintenance/backup/status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include'
      });
      const json = await res.json();
      if (json?.data) setBackupStatus(json.data);
    } catch {}
  }, [getAuthToken]);

  // автообновление статуса бэкапа
  React.useEffect(() => {
    pollBackupStatus();
    const id = setInterval(pollBackupStatus, 5000);
    return () => clearInterval(id);
  }, [pollBackupStatus]);

  const handleBackup = async () => {
    try {
      setBackupLoading(true);
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/maintenance/backup`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || 'Не удалось запустить бэкап');
      message.success('Резервное копирование запущено');
      pollBackupStatus();
    } catch (e: any) {
      message.error(e?.message || 'Ошибка запуска бэкапа');
    } finally {
      setBackupLoading(false);
    }
  };

  return (
    <PageLayout
      title="Администрирование"
      subtitle="Управление системой и настройки администратора"
    >
      {/* Статистика системы */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Statistic
              title="Пользователей"
              value={usersCount ?? 0}
              prefix={<UserOutlined className="text-blue-500" />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Statistic
              title="Активных сессий"
              value={activeSessions ?? 0}
              prefix={<SecurityScanOutlined className="text-green-500" />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Statistic
              title="Размер БД"
              value={typeof dbSize === 'number' ? dbSize : undefined}
              prefix={<DatabaseOutlined className="text-orange-500" />}
              valueStyle={{ color: '#fa8c16' }}
              suffix={typeof dbSize === 'string' ? dbSize : 'ГБ'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
            <Statistic
              title="Версия системы"
              value="2.0"
              prefix={<SettingOutlined className="text-purple-500" />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Очистка данных</div>
                <div style={{ color: 'var(--text-secondary)' }}>Удалить заявки, маршруты, грузы, документы и финансы</div>
              </div>
              <Button danger onClick={handlePurge}>Очистить</Button>
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Резервная копия БД</div>
                <div style={{ color: 'var(--text-secondary)' }}>
                  <Space direction="vertical" size={0}>
                    <span>Статус: {backupStatus?.ok === true ? <Tag color="green">Успешно</Tag> : backupStatus?.ok === false ? <Tag color="red">Ошибка</Tag> : <Tag>—</Tag>}</span>
                    {backupStatus?.startedAt && <span>Начало: {new Date(backupStatus.startedAt).toLocaleString()}</span>}
                    {backupStatus?.finishedAt && <span>Окончание: {new Date(backupStatus.finishedAt).toLocaleString()}</span>}
                    {backupStatus?.message && <span>Сообщение: {backupStatus.message}</span>}
                  </Space>
                </div>
              </div>
              <Button type="primary" loading={backupLoading} onClick={handleBackup}>Запустить бэкап</Button>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Административные функции */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card 
            title="Управление пользователями" 
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}
            extra={<Button type="link" style={{ color: '#1890ff' }} onClick={openUsers}>Управлять</Button>}
          >
            <div style={{ color: 'var(--text-secondary)' }}>
              <p>Создание, редактирование и удаление пользователей системы</p>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Роли и доступ" 
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}
            extra={<Button type="link" style={{ color: '#1890ff' }} onClick={openRolesAccess}>Настроить</Button>}
          >
            <div style={{ color: 'var(--text-secondary)' }}>
              <p>Матрица permissions по модулям (whitelist).</p>
              <p style={{ marginTop: 8 }}>
                <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate('/administration/roles')}>
                  Имена и описание ролей
                </Button>
              </p>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Журнал аудита" 
            style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}
            extra={<Button type="link" style={{ color: '#1890ff' }} onClick={openAudit}>Открыть</Button>}
          >
            <div style={{ color: 'var(--text-secondary)' }}>
              <p>Просмотр действий пользователей по заявкам, маршрутам, грузам и др.</p>
            </div>
          </Card>
        </Col>
      </Row>
    </PageLayout>
  );
};

export default AdministrationPage; 